import React from 'react'
import { useStore } from '../../store/store';
import { ItemTypes, viewerTypes, getPrimaryDark } from '../../constants/constants';
import { canPasteCopy } from '../../utils/copies';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useTranslation } from 'react-i18next';
import BoxButton from './BoxButton';
import GenerateDivider from './GenerateDivider';
import CopyItem from './CopyItem';
import DuotoneLightIcon from './DuotoneLightIcon';
import ShapesStarIcon from './ShapesStarIcon';
import { FAMILY_ACCENTS, FAMILY_BACKGROUNDS } from '../../shared/ui/editorAccents';
import { PALETTE_GRID_STYLE, PALETTE_TILE_ICON_SIZE } from '../../shared/ui/paletteTile';

const size = 38
const iconFontSize = PALETTE_TILE_ICON_SIZE
const secondIconFontSizeReduction = 10

// Fond des vignettes (panel gauche en mode grille, et boutons de l'AdderMenu
// en mode liste) : une teinte pastel par groupe plutôt que le blanc plat, pour
// distinguer visuellement Contenu/Données/Mise en page/Variables sans toucher
// aux icônes ni aux bordures (BORDER_GRAY, posée par BoxButton, inchangée).
// La table vit dans `src/shared/ui/editorAccents.js` : la palette du web-editor
// reprend les mêmes teintes, et deux copies auraient divergé.
const GROUP_BG_CONTENU = FAMILY_BACKGROUNDS.content;
const GROUP_BG_DONNEES = FAMILY_BACKGROUNDS.data;
const GROUP_BG_MISE_EN_PAGE = FAMILY_BACKGROUNDS.layout;
const GROUP_BG_VARIABLES = FAMILY_BACKGROUNDS.blocks;
// Titre de chaque groupe, dans la version soutenue de la teinte de ses tuiles.
const GROUP_ACCENT_CONTENU = FAMILY_ACCENTS.content;
const GROUP_ACCENT_DONNEES = FAMILY_ACCENTS.data;
const GROUP_ACCENT_MISE_EN_PAGE = FAMILY_ACCENTS.layout;
const GROUP_ACCENT_VARIABLES = FAMILY_ACCENTS.blocks;



const BoxButtonsWrapper = ({ onClick, showCopy = true, columnId, canDrag, fromViewer, fromAdder = false, dividerColor, showLabels = false }) => {
  const gridMode = showLabels;
  // En mode "showLabels" (panel gauche) : grille 3 colonnes uniforme. Les
  // wrappers intermediaires passent en `display:contents` pour que tous les
  // BoxButton deviennent des cellules de la meme grille (sinon chaque groupe
  // flex se reflow separement -> lignes fragmentees a 2 boutons).
  const bigProps = showLabels ? { showLabel: true, marginLeft: 0, marginBottom: 0 } : {};
  const rootStyle = gridMode
    ? PALETTE_GRID_STYLE
    : { display: 'flex', alignItems: 'center', flexWrap: 'wrap' };
  const groupStyle = gridMode
    ? { display: 'contents' }
    : { display: 'flex', alignItems: 'center', flexWrap: 'wrap' };
  const copiesRowStyle = gridMode
    ? { gridColumn: '1 / -1', display: 'flex', flexWrap: 'wrap', alignItems: 'center' }
    : { display: 'flex', flexWrap: 'wrap', alignItems: 'center' };
  const { t } = useTranslation();
  const currentDocument = useStore(({ currentDocument }) => currentDocument);
  const currentTemplate = useStore(({ currentTemplate }) => currentTemplate);
  const isOfferTemplate = currentDocument.type === 'template' && currentTemplate?.templatable === 'offer'
  const maskEditing = useStore(({ maskEditing }) => maskEditing);
  const copies = useStore(({ copies }) => copies);
  // Dans l'AdderMenu, seuls les types réellement collables à cet endroit sont
  // proposés (le panel de gauche, lui, sert aussi de source de glisser-déposer
  // vers les diapositives, où un groupe de box reste valide).
  const pastableCopies = fromAdder
    ? copies.filter(({ item }) => canPasteCopy(item, { columnId }))
    : copies;
  const isPreview = fromViewer === viewerTypes.PREVIEW
  const isLandscape = useStore(({ landscape }) => landscape)
  const accentColor = getPrimaryDark(currentDocument.type === 'template');
  const showVariablesGroup = (currentDocument.type === 'template' && maskEditing.length === 0) || isOfferTemplate;

  // Conditions d'affichage par bouton dans l'AdderMenu (mode non-grille) :
  // nommées une fois ici pour pouvoir regrouper les boutons par catégorie
  // (Contenu/Données/Mise en page/Variables, comme le panel gauche) sans
  // dupliquer la logique columnId/isPreview/isLandscape/fromAdder d'origine.
  const showContentGroup2 = !isPreview && (!isLandscape || !fromAdder);
  const showStructuralExtras = !columnId && !isPreview && (!isLandscape || !fromAdder);
  const showFilePdfLandscapeAdder = !columnId && !isPreview && isLandscape && fromAdder;
  const showSection = !columnId && !isPreview;
  const showLineBreak = !columnId && !isLandscape;
  const showSpacePreviewVariants = !columnId && isPreview && !isLandscape;
  const showSpaceDefault = !columnId && !isPreview && !isLandscape;
  const showMaskDraw = !columnId && !isPreview;
  const showVariablesBasic = showContentGroup2 && currentDocument.type === 'template' && maskEditing.length === 0;
  const showRecursiveModel = showContentGroup2 && isOfferTemplate;

  // Panel gauche (ElementsPanel, seul appelant avec showLabels) : jamais de
  // fromAdder/isPreview/columnId, donc regroupement dédié plus simple que le
  // flux ci-dessous, avec un ordre pensé par usage plutôt que par mécanique
  // interne des box (cf. AdderMenu, qui garde l'ordre/les 4 boutons de titre
  // d'origine, l'espace n'y étant pas contraint par une grille 3 colonnes).
  if (gridMode) {
    return (
      <div style={rootStyle}>
        <div style={groupStyle}>
          {showCopy && <>
            <GenerateDivider text='Éléments copiés' color={dividerColor} fullRow />
            {/* minHeight fixe (taille d'un CopyItem) : la rangée garde la même
                hauteur qu'il y ait 0 ou plusieurs copies, pour que le reste du
                panel (Eléments disponibles, Contenu...) ne se décale pas. */}
            <div style={{ ...copiesRowStyle, minHeight: size }}>
              {pastableCopies.map(({ sourceId, item }) => (
                <CopyItem
                  key={sourceId}
                  copy={item}
                  sourceId={sourceId}
                  onClick={(type) => onClick && onClick({ type, fromCopied: true, copy: item })}
                />
              ))}
            </div>
          </>}
          <GenerateDivider text='Eléments disponibles' color={dividerColor} fullRow />
        </div>

        <GenerateDivider text='Contenu' color={dividerColor} fullRow subtle accent={GROUP_ACCENT_CONTENU} />
        <BoxButton
          canDrag={canDrag} {...bigProps}
          onClick={() => onClick && onClick({ type: ItemTypes.TEXT })}
          type={ItemTypes.TEXT}
          text={t('text')}
          icon={<DuotoneLightIcon name='pen-fancy' style={{ fontSize: iconFontSize, color: accentColor }} />}
          bgcolor={GROUP_BG_CONTENU}
          disp='inline-block'
        />
        <BoxButton
          canDrag={canDrag} {...bigProps}
          onClick={() => onClick && onClick({ type: ItemTypes.SHAPE })}
          type={ItemTypes.SHAPE}
          text={t('shape')}
          icon={<ShapesStarIcon style={{ fontSize: iconFontSize, color: accentColor }} />}
          bgcolor={GROUP_BG_CONTENU}
          disp='inline-block'
        />
        {/* Plus d'élément « Ligne » : une ligne est une variante de l'élément
            « Forme », choisie dans son sélecteur de type (cf.
            ShapeKindSelector). Le type de box existe toujours — les documents
            déjà faits gardent leurs lignes. */}
        <BoxButton
          canDrag={canDrag} {...bigProps}
          onClick={() => onClick && onClick({ type: ItemTypes.IMG })}
          type={ItemTypes.IMG}
          text={t('image')}
          icon={<DuotoneLightIcon name='image' style={{ fontSize: iconFontSize, color: accentColor }} />}
          bgcolor={GROUP_BG_CONTENU}
          disp='inline-block'
        />
        <BoxButton
          canDrag={canDrag} {...bigProps}
          onClick={() => onClick && onClick({ type: ItemTypes.TITLE })}
          type={ItemTypes.TITLE}
          text={t('title')}
          icon={<FontAwesomeIcon icon={icon({ name: 't', style: 'solid' })} style={{ fontSize: iconFontSize, color: accentColor }} />}
          bgcolor={GROUP_BG_CONTENU}
          disp='inline-block'
        />
        <BoxButton
          canDrag={canDrag} {...bigProps}
          onClick={() => onClick && onClick({ type: ItemTypes.SUMMARY })}
          type={ItemTypes.SUMMARY}
          text={t('summary')}
          icon={<span className="fa-layers" style={{ width: '27px' }}>
            <FontAwesomeIcon icon={icon({ name: 'square-dashed', style: 'light' })} style={{ fontSize: iconFontSize + 6, color: accentColor, opacity: 0.6 }} />
            <FontAwesomeIcon icon={icon({ name: 'list-ol', style: 'light' })} style={{ fontSize: iconFontSize - secondIconFontSizeReduction, color: accentColor }} />
          </span>}
          bgcolor={GROUP_BG_CONTENU}
          disp='inline-block'
        />

        {/* Box partagées avec l'éditeur de site : le parser les convertit en
            vrai volet dépliable / vraie carte (chantier-boxes-partagees.md). */}
        <BoxButton
          canDrag={canDrag} {...bigProps}
          onClick={() => onClick && onClick({ type: ItemTypes.ACCORDION_PANEL })}
          type={ItemTypes.ACCORDION_PANEL}
          text={t('accordion_panel')}
          icon={< FontAwesomeIcon icon={icon({ name: 'rectangle-history', family: 'duotone', style: 'solid' })} style={{ fontSize: iconFontSize, color: accentColor }} />}
          bgcolor={GROUP_BG_CONTENU}
          disp='inline-block'
        />
        <BoxButton
          canDrag={canDrag} {...bigProps}
          onClick={() => onClick && onClick({ type: ItemTypes.INFO_CARD })}
          type={ItemTypes.INFO_CARD}
          text={t('info_card')}
          icon={< FontAwesomeIcon icon={icon({ name: 'address-card', family: 'duotone', style: 'solid' })} style={{ fontSize: iconFontSize, color: accentColor }} />}
          bgcolor={GROUP_BG_CONTENU}
          disp='inline-block'
        />

        <GenerateDivider text='Données' color={dividerColor} fullRow subtle accent={GROUP_ACCENT_DONNEES} />
        <BoxButton
          canDrag={canDrag} {...bigProps}
          onClick={() => onClick && onClick({ type: ItemTypes.TABLE })}
          type={ItemTypes.TABLE}
          text={t('table')}
          icon={<DuotoneLightIcon name='table' style={{ fontSize: iconFontSize, color: accentColor }} />}
          bgcolor={GROUP_BG_DONNEES}
          disp='inline-block'
        />
        <BoxButton
          canDrag={canDrag} {...bigProps}
          onClick={() => onClick && onClick({ type: ItemTypes.ORG_CHART })}
          type={ItemTypes.ORG_CHART}
          text={t('org_chart')}
          icon={< FontAwesomeIcon icon={icon({ name: 'sitemap', style: 'light', family: 'duotone' })} style={{ fontSize: iconFontSize, color: accentColor }} />}
          bgcolor={GROUP_BG_DONNEES}
          disp='inline-block'
        />
        <BoxButton
          canDrag={canDrag} {...bigProps}
          onClick={() => onClick && onClick({ type: ItemTypes.FILE_PDF })}
          type={ItemTypes.FILE_PDF}
          text={"PDF"}
          icon={<DuotoneLightIcon name='file-pdf' style={{ fontSize: iconFontSize, color: accentColor }} />}
          bgcolor={GROUP_BG_DONNEES}
          disp='inline-block'
        />
        <BoxButton
          canDrag={canDrag} {...bigProps}
          onClick={() => onClick && onClick({ type: ItemTypes.SECTION })}
          type={ItemTypes.SECTION}
          text={t('section')}
          icon={< FontAwesomeIcon icon={icon({ name: 'rectangle-history-circle-plus', family: 'duotone', style: 'solid' })} style={{ fontSize: iconFontSize, color: accentColor }} />}
          bgcolor={GROUP_BG_DONNEES}
          disp='inline-block'
        />

        <GenerateDivider text='Mise en page' color={dividerColor} fullRow subtle accent={GROUP_ACCENT_MISE_EN_PAGE} />
        {!isLandscape && <BoxButton
          canDrag={canDrag} {...bigProps}
          onClick={() => onClick && onClick({ type: ItemTypes.LINE_BREAK })}
          type={ItemTypes.LINE_BREAK}
          text={t('line_break')}
          icon={< FontAwesomeIcon icon={icon({ name: 'turn-down-left', family: 'duotone', style: 'solid' })} style={{ fontSize: iconFontSize, color: accentColor }} />}
          bgcolor={GROUP_BG_MISE_EN_PAGE}
          disp='inline-block'
        />}
        {!isLandscape && <BoxButton
          canDrag={canDrag} {...bigProps}
          onClick={() => onClick && onClick({ type: ItemTypes.SPACE })}
          type={ItemTypes.SPACE}
          text={t('space')}
          icon={<span className="fa-layers" style={{ width: '27px' }}>
            <FontAwesomeIcon icon={icon({ name: 'square-dashed', style: 'light' })} style={{ fontSize: iconFontSize + 6, color: accentColor, opacity: 0.6 }} />
            <FontAwesomeIcon icon={icon({ name: 'arrow-up-right-and-arrow-down-left-from-center', style: 'light' })} style={{ fontSize: iconFontSize - 13, transform: 'rotate(135deg)', color: accentColor }} />
          </span>}
          bgcolor={GROUP_BG_MISE_EN_PAGE}
          disp='inline-block'
        />}
        <BoxButton
          canDrag={canDrag} {...bigProps}
          onClick={() => onClick && onClick({ type: ItemTypes.MASK })}
          type={ItemTypes.MASK}
          text={t('mask')}
          icon={<span className="fa-layers" style={{ width: '27px' }}>
            <FontAwesomeIcon icon={icon({ name: 'square-dashed', style: 'light' })} style={{ fontSize: iconFontSize + 6, color: accentColor, opacity: 0.6 }} />
            <FontAwesomeIcon icon={icon({ name: 'masks-theater', style: 'light' })} style={{ fontSize: iconFontSize - secondIconFontSizeReduction - 2, color: accentColor }} />
          </span>}
          bgcolor={GROUP_BG_MISE_EN_PAGE}
          disp='inline-block'
        />
        <BoxButton
          canDrag={canDrag} {...bigProps}
          onClick={() => onClick && onClick({ type: ItemTypes.DRAW })}
          type={ItemTypes.DRAW}
          text={isLandscape ? t('new_slide') : t('drawing')}
          icon={<span className="fa-layers" style={{ width: '27px' }}>
            <FontAwesomeIcon icon={icon({ name: 'square-dashed', style: 'light' })} style={{ fontSize: iconFontSize + 6, color: accentColor, opacity: 0.6 }} />
            <FontAwesomeIcon icon={icon({ name: 'scribble', style: 'light' })} style={{ fontSize: iconFontSize - secondIconFontSizeReduction, color: accentColor }} />
          </span>}
          bgcolor={GROUP_BG_MISE_EN_PAGE}
          disp='inline-block'
        />

        {showVariablesGroup && <>
          <GenerateDivider text='Variables' color={dividerColor} fullRow subtle accent={GROUP_ACCENT_VARIABLES} />
          {currentDocument.type === 'template' && maskEditing.length === 0 && <>
            <BoxButton
              canDrag={canDrag} {...bigProps}
              onClick={() => onClick && onClick({ type: ItemTypes.SELECTOR_VARIABLE })}
              type={ItemTypes.SELECTOR_VARIABLE}
              text={t('selector_variable')}
              icon={<DuotoneLightIcon name='list-dropdown' style={{ fontSize: iconFontSize, color: accentColor }} />}
              bgcolor={GROUP_BG_VARIABLES}
              disp='inline-block'
            />
            <BoxButton
              canDrag={canDrag} {...bigProps}
              onClick={() => onClick && onClick({ type: ItemTypes.CHECKBOXS_VARIABLE })}
              type={ItemTypes.CHECKBOXS_VARIABLE}
              text={t('checkboxs_variable')}
              icon={<span className="fa-layers" style={{ width: '28px', height: '28px', fontSize: '28px' }}>
                <FontAwesomeIcon icon={icon({ name: 'square', style: 'light' })} style={{ fontSize: iconFontSize, color: accentColor }} />
                <FontAwesomeIcon icon={icon({ name: 'check', style: 'solid' })} style={{ fontSize: iconFontSize - 10, color: accentColor, opacity: 0.55 }} />
              </span>}
              bgcolor={GROUP_BG_VARIABLES}
              disp='inline-block'
            />
          </>}
          {isOfferTemplate && <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({ type: ItemTypes.RECURSIVE_MODEL })}
            type={ItemTypes.RECURSIVE_MODEL}
            text={t('recursive_model')}
            icon={<DuotoneLightIcon name='layer-group' style={{ fontSize: iconFontSize, color: accentColor }} />}
            bgcolor={GROUP_BG_VARIABLES}
            disp='inline-block'
          />}
        </>}
      </div>
    );
  }

  return (
    <div style={rootStyle}>
      <div style={groupStyle}>
        {showCopy && <>
          <GenerateDivider text='Éléments copiés' color={dividerColor} fullRow={gridMode} />
          {pastableCopies.length > 0 ? (
            // Les copies sont des vignettes de taille fixe : en mode grille elles
            // occupent une ligne entière plutôt qu'une cellule chacune, sinon
            // elles casseraient l'alignement des boutons pleine largeur.
            <div style={copiesRowStyle}>
              {pastableCopies.map(({ sourceId, item }) => (
                <CopyItem
                  key={sourceId}
                  copy={item}
                  sourceId={sourceId}
                  onClick={(type) => onClick && onClick({ type, fromCopied: true, copy: item })}
                />
              ))}
            </div>
          ) : (
            <div
              style={{
                display: gridMode ? 'none' : undefined,
                marginLeft: '5px',
                width: size,
                height: size,
                borderRadius: '5px',
                fontSize: '9px',
                fontFamily: 'Montserrat',
                color: '#000000',
                textAlign: 'center',
              }}
            ></div>
          )}
        </>}
        <GenerateDivider text='Eléments disponibles' color={dividerColor} fullRow={gridMode} />
        {/* Boutons regroupés par catégorie (Contenu/Données/Mise en page/
            Variables), même logique de couleur que le panel gauche, sans
            séparateurs textuels — chaque bouton garde sa condition d'affichage
            d'origine (columnId/isPreview/isLandscape/fromAdder), nommée dans
            les show* ci-dessus. */}

        {/* Contenu */}
        {showContentGroup2 && <>
          <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({ type: ItemTypes.TEXT })}
            type={ItemTypes.TEXT}
            text={t('text')}
            icon={<DuotoneLightIcon name='pen-fancy' style={{ fontSize: iconFontSize, color: accentColor }} />}
            bgcolor={GROUP_BG_CONTENU}
            disp='inline-block'
          />
          <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({ type: ItemTypes.SHAPE })}
            type={ItemTypes.SHAPE}
            text={t('shape')}
            icon={<ShapesStarIcon style={{ fontSize: iconFontSize, color: accentColor }} />}
            bgcolor={GROUP_BG_CONTENU}
            disp='inline-block'
          />
          <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({ type: ItemTypes.IMG })}
            type={ItemTypes.IMG}
            text={t('image')}
            icon={<DuotoneLightIcon name='image' style={{ fontSize: iconFontSize, color: accentColor }} />}
            bgcolor={GROUP_BG_CONTENU}
            disp='inline-block'
          />
        </>}
        {showContentGroup2 && <>
          {/* Box partagée avec l'éditeur de site : le parser la convertit en
              vrai volet dépliable (chantier-boxes-partagees.md). */}
          <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({ type: ItemTypes.ACCORDION_PANEL })}
            type={ItemTypes.ACCORDION_PANEL}
            text={t('accordion_panel')}
            icon={< FontAwesomeIcon icon={icon({ name: 'rectangle-history', family: 'duotone', style: 'solid' })} style={{ fontSize: iconFontSize, color: accentColor }} />}
            bgcolor={GROUP_BG_CONTENU}
            disp='inline-block'
          />
          <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({ type: ItemTypes.INFO_CARD })}
            type={ItemTypes.INFO_CARD}
            text={t('info_card')}
            icon={< FontAwesomeIcon icon={icon({ name: 'address-card', family: 'duotone', style: 'solid' })} style={{ fontSize: iconFontSize, color: accentColor }} />}
            bgcolor={GROUP_BG_CONTENU}
            disp='inline-block'
          />
        </>}
        {showStructuralExtras && <>
          <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({ type: ItemTypes.TITLE })}
            type={ItemTypes.TITLE}
            text={t('title1')}
            icon={<span className="fa-layers" style={{ width: '21px' }}>
              <FontAwesomeIcon icon={icon({ name: 't', style: 'solid' })} style={{ fontSize: iconFontSize, color: accentColor, opacity: 0.6 }} />
              <FontAwesomeIcon icon={icon({ name: '1', family: 'duotone', style: 'solid' })} style={{ fontSize: iconFontSize - secondIconFontSizeReduction, position: 'relative', left: '8px', color: accentColor, '--fa-secondary-opacity': 1 }} />
            </span>}
            bgcolor={GROUP_BG_CONTENU}
            disp='inline-block'
          />
          <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({ type: ItemTypes.TITLE_2 })}
            type={ItemTypes.TITLE_2}
            text={t('title2')}
            icon={<span className="fa-layers" style={{ width: '21px' }}>
              <FontAwesomeIcon icon={icon({ name: 't', style: 'solid' })} style={{ fontSize: iconFontSize, color: accentColor, opacity: 0.6 }} />
              <FontAwesomeIcon icon={icon({ name: '2', family: 'duotone', style: 'solid' })} style={{ fontSize: iconFontSize - secondIconFontSizeReduction, position: 'relative', left: '8px', color: accentColor, '--fa-secondary-opacity': 1 }} />
            </span>}
            bgcolor={GROUP_BG_CONTENU}
            disp='inline-block'
          />
          <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({ type: ItemTypes.TITLE_3 })}
            type={ItemTypes.TITLE_3}
            text={t('title3')}
            icon={<span className="fa-layers" style={{ width: '21px' }}>
              <FontAwesomeIcon icon={icon({ name: 't', style: 'solid' })} style={{ fontSize: iconFontSize, color: accentColor, opacity: 0.6 }} />
              <FontAwesomeIcon icon={icon({ name: '3', family: 'duotone', style: 'solid' })} style={{ fontSize: iconFontSize - secondIconFontSizeReduction, position: 'relative', left: '8px', color: accentColor, '--fa-secondary-opacity': 1 }} />
            </span>}
            bgcolor={GROUP_BG_CONTENU}
            disp='inline-block'
          />
          <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({ type: ItemTypes.TITLE_4 })}
            type={ItemTypes.TITLE_4}
            text={t('title4')}
            icon={<span className="fa-layers" style={{ width: '21px' }}>
              <FontAwesomeIcon icon={icon({ name: 't', style: 'solid' })} style={{ fontSize: iconFontSize, color: accentColor, opacity: 0.6 }} />
              <FontAwesomeIcon icon={icon({ name: '4', family: 'duotone', style: 'solid' })} style={{ fontSize: iconFontSize - secondIconFontSizeReduction, position: 'relative', left: '8px', color: accentColor, '--fa-secondary-opacity': 1 }} />
            </span>}
            bgcolor={GROUP_BG_CONTENU}
            disp='inline-block'
          />
          <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({ type: ItemTypes.SUMMARY })}
            type={ItemTypes.SUMMARY}
            text={t('summary')}
            icon={<span className="fa-layers" style={{ width: '27px' }}>
              <FontAwesomeIcon icon={icon({ name: 'square-dashed', style: 'light' })} style={{ fontSize: iconFontSize + 6, color: accentColor, opacity: 0.6 }} />
              <FontAwesomeIcon icon={icon({ name: 'list-ol', style: 'light' })} style={{ fontSize: iconFontSize - secondIconFontSizeReduction, color: accentColor }} />
            </span>}
            bgcolor={GROUP_BG_CONTENU}
            disp='inline-block'
          />
        </>}

        {/* Force un retour à la ligne entre catégories (flex-wrap ne le fait
            que si le contenu déborde) : un séparateur visuel sans divider ni
            texte, juste la couleur qui change proprement de rangée. */}
        <div style={{ flexBasis: '100%', height: 0 }} />

        {/* Données */}
        {showStructuralExtras && <>
          <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({ type: ItemTypes.TABLE })}
            type={ItemTypes.TABLE}
            text={t('table')}
            icon={<DuotoneLightIcon name='table' style={{ fontSize: iconFontSize, color: accentColor }} />}
            bgcolor={GROUP_BG_DONNEES}
            disp='inline-block'
          />
          <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({ type: ItemTypes.ORG_CHART })}
            type={ItemTypes.ORG_CHART}
            text={t('org_chart')}
            icon={< FontAwesomeIcon icon={icon({ name: 'sitemap', style: 'light', family: 'duotone' })} style={{ fontSize: iconFontSize, color: accentColor }} />}
            bgcolor={GROUP_BG_DONNEES}
            disp='inline-block'
          />
          <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({ type: ItemTypes.FILE_PDF })}
            type={ItemTypes.FILE_PDF}
            text={"PDF"}
            icon={<DuotoneLightIcon name='file-pdf' style={{ fontSize: iconFontSize, color: accentColor }} />}
            bgcolor={GROUP_BG_DONNEES}
            disp='inline-block'
          />
        </>}
        {showFilePdfLandscapeAdder && <BoxButton
          canDrag={canDrag} {...bigProps}
          onClick={() => onClick && onClick({ type: ItemTypes.FILE_PDF })}
          type={ItemTypes.FILE_PDF}
          text={"PDF"}
          icon={< FontAwesomeIcon icon={icon({ name: 'file-pdf', family: 'duotone', style: 'solid' })} style={{ fontSize: iconFontSize, color: accentColor }} />}
          bgcolor={GROUP_BG_DONNEES}
          disp='inline-block'
        />}
        {showSection && <BoxButton
          canDrag={canDrag} {...bigProps}
          onClick={() => onClick && onClick({ type: ItemTypes.SECTION })}
          type={ItemTypes.SECTION}
          text={t('section')}
          icon={< FontAwesomeIcon icon={icon({ name: 'rectangle-history-circle-plus', family: 'duotone', style: 'solid' })} style={{ fontSize: iconFontSize, color: accentColor }} />}
          bgcolor={GROUP_BG_DONNEES}
          disp='inline-block'
        />}

        <div style={{ flexBasis: '100%', height: 0 }} />

        {/* Mise en page */}
        {showLineBreak && <BoxButton
          canDrag={canDrag} {...bigProps}
          onClick={() => onClick && onClick({ type: ItemTypes.LINE_BREAK })}
          type={ItemTypes.LINE_BREAK}
          text={t('line_break')}
          icon={< FontAwesomeIcon icon={icon({ name: 'turn-down-left', family: 'duotone', style: 'solid' })} style={{ fontSize: iconFontSize, color: accentColor }} />}
          bgcolor={GROUP_BG_MISE_EN_PAGE}
          disp='inline-block'
        />}
        {showSpacePreviewVariants && <>
          <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({ type: ItemTypes.SPACE })}
            type={ItemTypes.SPACE}
            text={t('space')}
            icon={<div>
              <span className="fa-layers" style={{ width: '21px' }}>
                <FontAwesomeIcon icon={icon({ name: 'square-dashed', family: 'duotone', style: 'solid' })} style={{ fontSize: 15, color: accentColor }} />
                <FontAwesomeIcon icon={icon({ name: 'arrow-up-right-and-arrow-down-left-from-center', family: 'duotone', style: 'solid' })} style={{ fontSize: 6.5, transform: 'rotate(135deg)', color: accentColor }} />
              </span>
            </div>}
            bgcolor={GROUP_BG_MISE_EN_PAGE}
            disp='inline-block'
          />
          <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({
              type: ItemTypes.SPACE,
              defaultValues: { content: { sizeName: 'medium' } }
            })}
            type={ItemTypes.SPACE}
            text={t('space')}
            icon={<div>
              <span className="fa-layers" style={{ width: '21px' }}>
                <FontAwesomeIcon icon={icon({ name: 'square-dashed', style: 'solid' })} style={{ fontSize: 20, color: accentColor }} />
                <FontAwesomeIcon icon={icon({ name: 'arrow-up-right-and-arrow-down-left-from-center', family: 'duotone', style: 'solid' })} style={{ fontSize: 8.8, transform: 'rotate(135deg)', color: accentColor }} />
              </span>
            </div>}
            bgcolor={GROUP_BG_MISE_EN_PAGE}
            disp='inline-block'
          />
          <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({
              type: ItemTypes.SPACE,
              defaultValues: { content: { sizeName: 'large' } }
            })}
            type={ItemTypes.SPACE}
            text={t('space')}
            icon={<div>
              <span className="fa-layers" style={{ width: '22px' }}>
                <FontAwesomeIcon icon={icon({ name: 'square-dashed', style: 'solid' })} style={{ fontSize: iconFontSize, color: accentColor, opacity: 0.6 }} />
                <FontAwesomeIcon icon={icon({ name: 'arrow-up-right-and-arrow-down-left-from-center', family: 'duotone', style: 'solid' })} style={{ fontSize: iconFontSize - 14, transform: 'rotate(135deg)', color: accentColor }} />
              </span>
            </div>}
            bgcolor={GROUP_BG_MISE_EN_PAGE}
            disp='inline-block'
          />
        </>}
        {showSpaceDefault && <BoxButton
          canDrag={canDrag} {...bigProps}
          onClick={() => onClick && onClick({ type: ItemTypes.SPACE })}
          type={ItemTypes.SPACE}
          text={t('space')}
          icon={<span className="fa-layers" style={{ width: '27px' }}>
            <FontAwesomeIcon icon={icon({ name: 'square-dashed', style: 'light' })} style={{ fontSize: iconFontSize + 6, color: accentColor, opacity: 0.6 }} />
            <FontAwesomeIcon icon={icon({ name: 'arrow-up-right-and-arrow-down-left-from-center', style: 'light' })} style={{ fontSize: iconFontSize - 13, transform: 'rotate(135deg)', color: accentColor }} />
          </span>}
          bgcolor={GROUP_BG_MISE_EN_PAGE}
          disp='inline-block'
        />}
        {showMaskDraw && <>
          <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({ type: ItemTypes.MASK })}
            type={ItemTypes.MASK}
            text={t('mask')}
            icon={<span className="fa-layers" style={{ width: '27px' }}>
              <FontAwesomeIcon icon={icon({ name: 'square-dashed', style: 'light' })} style={{ fontSize: iconFontSize + 6, color: accentColor, opacity: 0.6 }} />
              <FontAwesomeIcon icon={icon({ name: 'masks-theater', style: 'light' })} style={{ fontSize: iconFontSize - secondIconFontSizeReduction - 2, color: accentColor }} />
            </span>}
            bgcolor={GROUP_BG_MISE_EN_PAGE}
            disp='inline-block'
          />
          <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({ type: ItemTypes.DRAW })}
            type={ItemTypes.DRAW}
            text={isLandscape ? t('new_slide') : t('drawing')}
            icon={<span className="fa-layers" style={{ width: '27px' }}>
              <FontAwesomeIcon icon={icon({ name: 'square-dashed', style: 'light' })} style={{ fontSize: iconFontSize + 6, color: accentColor, opacity: 0.6 }} />
              <FontAwesomeIcon icon={icon({ name: 'scribble', style: 'light' })} style={{ fontSize: iconFontSize - secondIconFontSizeReduction, color: accentColor }} />
            </span>}
            bgcolor={GROUP_BG_MISE_EN_PAGE}
            disp='inline-block'
          />
        </>}

        <div style={{ flexBasis: '100%', height: 0 }} />

        {/* Variables */}
        {showVariablesBasic && <>
          <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({ type: ItemTypes.SELECTOR_VARIABLE })}
            type={ItemTypes.SELECTOR_VARIABLE}
            text={t('selector_variable')}
            icon={<DuotoneLightIcon name='list-dropdown' style={{ fontSize: iconFontSize, color: accentColor }} />}
            bgcolor={GROUP_BG_VARIABLES}
            disp='inline-block'
          />
          <BoxButton
            canDrag={canDrag} {...bigProps}
            onClick={() => onClick && onClick({ type: ItemTypes.CHECKBOXS_VARIABLE })}
            type={ItemTypes.CHECKBOXS_VARIABLE}
            text={t('checkboxs_variable')}
            icon={<span className="fa-layers" style={{ width: '28px', height: '28px', fontSize: '28px' }}>
              <FontAwesomeIcon icon={icon({ name: 'square', style: 'light' })} style={{ fontSize: iconFontSize, color: accentColor }} />
              <FontAwesomeIcon icon={icon({ name: 'check', style: 'solid' })} style={{ fontSize: iconFontSize - 10, color: accentColor, opacity: 0.55 }} />
            </span>}
            bgcolor={GROUP_BG_VARIABLES}
            disp='inline-block'
          />
        </>}
        {showRecursiveModel && <BoxButton
          canDrag={canDrag} {...bigProps}
          onClick={() => onClick && onClick({ type: ItemTypes.RECURSIVE_MODEL })}
          type={ItemTypes.RECURSIVE_MODEL}
          text={t('recursive_model')}
          icon={<DuotoneLightIcon name='layer-group' style={{ fontSize: iconFontSize, color: accentColor }} />}
          bgcolor={GROUP_BG_VARIABLES}
          disp='inline-block'
        />}
      </div>
    </div >
  )
}

export default BoxButtonsWrapper
